import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ThreadMessagesPage.css';

const ThreadMessagesPage: React.FC = () => {
  const { threadId, userId } = useParams<{
    threadId: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const [threadMessages, setThreadMessages] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);

  useEffect(() => {
    if (threadId) {
      fetchThreadMessages(threadId);
    }
  }, [threadId]);

  const fetchThreadMessages = async (
    threadId: string,
    evaluatedKey?: string
  ) => {
    try {
      setLoading(!evaluatedKey);
      setLoadingMore(!!evaluatedKey);
      const token = localStorage.getItem('authToken');

      let url = `${process.env.REACT_APP_API_BASE_URL}/messages-v3/tool/threads/${threadId}?limit=100&newest_first=true`;
      if (evaluatedKey) {
        url += `&last_evaluated_key=${encodeURIComponent(evaluatedKey)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (evaluatedKey && threadMessages) {
          setThreadMessages({
            ...data,
            messages: [...threadMessages.messages, ...data.messages],
          });
        } else {
          setThreadMessages(data);
        }
        setLastEvaluatedKey(data.last_evaluated_key || null);
      } else {
        setThreadMessages(null);
      }
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      setThreadMessages(null);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  return (
    <div className="thread-messages-page">
      <div className="thread-header">
        <div className="thread-header-content">
          <button
            onClick={() => navigate(`/all-users?userId=${userId}`)}
            className="back-button"
          >
            ← Back to Users
          </button>
          <h1>Thread Messages</h1>
          <div className="thread-info">
            <span>Thread ID: {threadId}</span>
            <span>User ID: {userId}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading messages...</span>
        </div>
      ) : threadMessages ? (
        <div className="messages-wrapper">
          <div className="messages-container">
            {threadMessages.messages.map((message: any) => {
              const isFromUser = message.creator_id === userId;
              const messageDate = new Date(message.created_at).toLocaleString();
              return (
                <div
                  key={message.id}
                  className={`message ${
                    isFromUser ? 'user-message' : 'ai-message'
                  }`}
                >
                  <div className="message-content">{message.content}</div>
                  <div className="message-time">{messageDate}</div>
                </div>
              );
            })}
            {lastEvaluatedKey && (
              <div className="load-more-container">
                <button
                  onClick={() =>
                    fetchThreadMessages(threadId!, lastEvaluatedKey)
                  }
                  disabled={loadingMore}
                  className="load-more-button"
                >
                  {loadingMore ? (
                    <>
                      <div className="loading-spinner small"></div>
                      Loading more...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="no-messages">
          <p>No messages found for this thread</p>
        </div>
      )}
    </div>
  );
};

export default ThreadMessagesPage;
